/**
 * Players page: an ESTIMATE of what an affiliate earns for one player.
 *
 * The affiliates platform pays commission per affiliate, per brand and per calendar month, and its API exposes no
 * per-player figures (its CPA report says so outright). So a player's share is estimated here from the player's own
 * lifetime figures and the plan the affiliate is on:
 *
 * - **Rev share** = the player's NGR x the plan's rate. A tiered plan uses its first tier (the platform picks the tier
 *   on the affiliate's whole month, so this is the floor). Signed: a player who won money lowers the affiliate's take.
 * - **CPA** = the plan's amount once the player's lifetime deposits reach the plan's minimum. Only in the page's
 *   currency: a plan priced in another currency can't be compared to the player's deposits, so it stays unknown.
 * - A fixed fee is charged per affiliate per month, not per player, so it is never split across players: the plan's
 *   fee is shown as its terms, and every per-player figure leaves it out.
 *
 * What it can't see (it is why the UI marks every figure as an estimate): tier uplift, the amount frozen at
 * qualification, negative-month netting, manual adjustments, and the platform's own metric definitions.
 */
import type { Player } from '../../mock-data/types';
import type { CommissionAssignment, CommissionPlan, CommissionStructure } from '../affiliates/types';
import { isAffiliateCampaignNo } from '../creatives/affiliate-campaign';

/** The plan's config values come back verbatim, possibly as strings. */
const numberOf = (value: unknown): number | undefined => {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : typeof value === 'number' ? value : NaN;
  return Number.isFinite(n) ? n : undefined;
};

/**
 * The plan in force for the brand, or null when none is. When the read named the brand, the platform's own answer
 * wins, a null included (`resolved_for_brand_id` says the brand was asked about, and `resolved: null` then means no
 * assignment is in force for it, e.g. one that only starts later). Only a read that never named a brand falls back to
 * the precedence the platform documents: the affiliate's own brand assignment, its own all-brands default, then a
 * group's brand assignment, then a group's default.
 */
export const planInForce = (structure: CommissionStructure, brandId: string | undefined): CommissionPlan | null => {
  if (structure.resolved_for_brand_id) return structure.resolved?.plan ?? null;
  if (structure.resolved) return structure.resolved.plan;
  const pick = (list: readonly CommissionAssignment[] | undefined): CommissionAssignment | undefined =>
    (brandId ? list?.find((a) => a.brand_id === brandId) : undefined) ?? list?.find((a) => a.brand_id == null);
  return (pick(structure.assignments) ?? pick(structure.group_assignments))?.plan ?? null;
};

export type CommissionEstimate = {
  /** CPA plans only: the player's lifetime deposits reach the plan's minimum. Undefined when it can't be judged. */
  cpaQualified?: boolean;
  /** Each part is 0 when the plan doesn't pay it, undefined when it does but the inputs are unknown. */
  cpa?: number;
  revshare?: number;
  /** `cpa + revshare`, undefined when either is. */
  total?: number;
};

/** The rev share rate, in percent: a tiered plan's first tier, else the flat rate. */
export const ratePercent = (plan: CommissionPlan): number | undefined => {
  if (plan.tiered && plan.tiers && plan.tiers.length > 0) {
    const first = [...plan.tiers].sort((a, b) => a.min_revenue - b.min_revenue)[0];
    return numberOf(first.percent);
  }
  return numberOf(plan.config?.percent);
};

/** Estimate one player's commission under a plan. Undefined for a plan with no per-player share (a fixed fee). */
export const estimateCommission = (player: Player, plan: CommissionPlan): CommissionEstimate | undefined => {
  const paysRevshare = plan.type === 'revshare' || plan.type === 'hybrid';
  const paysCpa = plan.type === 'cpa' || plan.type === 'hybrid';
  if (!paysRevshare && !paysCpa) return undefined;

  let revshare: number | undefined = 0;
  if (paysRevshare) {
    const percent = ratePercent(plan);
    revshare = percent === undefined || player.ngr === undefined ? undefined : (player.ngr * percent) / 100;
  }

  let cpa: number | undefined = 0;
  let cpaQualified: boolean | undefined;
  if (paysCpa) {
    const currency = typeof plan.config?.cpa_currency === 'string' ? plan.config.cpa_currency.trim().toUpperCase() : '';
    const amount = numberOf(plan.config?.amount);
    if ((currency === '' || currency === 'USD') && amount !== undefined) {
      const minDeposit = numberOf(plan.config?.cpa_min_deposit) ?? 0;
      cpaQualified = player.depositsCount > 0 && player.deposits >= minDeposit;
      cpa = cpaQualified ? amount : 0;
    } else {
      cpa = undefined;
    }
  }

  return {
    ...(paysCpa ? { cpaQualified } : {}),
    cpa,
    revshare,
    total: cpa === undefined || revshare === undefined ? undefined : cpa + revshare,
  };
};

/** What the card and the sheet show for a player's commission. */
export type CommissionView =
  /** Not an affiliate player: no commission applies. */
  | { kind: 'none' }
  /** The affiliate or its plan is still being read. */
  | { kind: 'pending' }
  /** The affiliate's plan couldn't be read (not in the brand's list, or the read failed). */
  | { kind: 'unknown' }
  /** The affiliate is on no plan. */
  | { kind: 'no-plan' }
  /** `estimate` is undefined for a plan with no per-player share; `paysCpa` / `paysRevshare` say which parts apply.
   *  `revsharePercent`: the plan's rate (a tiered plan's first tier, `tiered` then set), the one the estimate used. */
  | {
      kind: 'estimate';
      planName: string;
      estimate?: CommissionEstimate;
      paysCpa: boolean;
      paysRevshare: boolean;
      revsharePercent?: number;
      tiered: boolean;
      /** The plan's fixed fee as set (per affiliate, per month), shown as terms and never split per player. */
      fixedFee?: FixedFee;
    };

/** A plan's monthly fixed fee: its amount, currency (absent = the platform's base) and whether a part month is
 *  prorated. */
export type FixedFee = { amount: number; currency?: string; prorated: boolean };

/** The plan's fixed fee, when it has one (any plan type can carry it). */
export const fixedFeeOf = (plan: CommissionPlan): FixedFee | undefined => {
  const amount = numberOf(plan.config?.fixed_fee_amount);
  if (amount === undefined || amount <= 0) return undefined;
  const currency = typeof plan.config?.fixed_fee_currency === 'string' ? plan.config.fixed_fee_currency.trim() : '';
  return {
    amount,
    ...(currency ? { currency: currency.toUpperCase() } : {}),
    prorated: plan.config?.fixed_fee_mode === 'prorated',
  };
};

/**
 * What the brand keeps of a player once the affiliate is paid, ESTIMATED: NGR less the estimated total commission.
 * Undefined when no commission applies (not an affiliate player, no plan, a plan with no per-player share) or when
 * either side is unknown.
 */
export const netAfterCommission = (player: Player, view: CommissionView): number | undefined => {
  if (view.kind !== 'estimate' || player.ngr === undefined) return undefined;
  const total = view.estimate?.total;
  return total === undefined ? undefined : player.ngr - total;
};

/** What the page knows about the brand's affiliates and their plans. */
export type CommissionSources = {
  /** The affiliates list is still being read. */
  directoryPending: boolean;
  /** Affiliate number (what a player stores) to the platform's affiliate id. */
  idByNo: ReadonlyMap<string, string>;
  /** Plans by affiliate id: null = no plan; absent = not read (yet, or the read failed). */
  plans: ReadonlyMap<string, CommissionPlan | null>;
  plansPending: boolean;
};

/** A player's commission view. Pure. */
export const commissionViewOf = (player: Player, sources: CommissionSources): CommissionView => {
  if (!isAffiliateCampaignNo(player.campaign) || !player.affiliate) return { kind: 'none' };
  if (sources.directoryPending) return { kind: 'pending' };
  const id = sources.idByNo.get(player.affiliate);
  if (id === undefined) return { kind: 'unknown' };
  const plan = sources.plans.get(id);
  if (plan === undefined) return sources.plansPending ? { kind: 'pending' } : { kind: 'unknown' };
  if (plan === null) return { kind: 'no-plan' };
  return {
    kind: 'estimate',
    planName: plan.name,
    estimate: estimateCommission(player, plan),
    paysCpa: plan.type === 'cpa' || plan.type === 'hybrid',
    paysRevshare: plan.type === 'revshare' || plan.type === 'hybrid',
    revsharePercent: ratePercent(plan),
    tiered: plan.tiered === true && (plan.tiers?.length ?? 0) > 0,
    fixedFee: fixedFeeOf(plan),
  };
};
